import { PROJECT_STATUS, PROJECT_STATUS_LABELS } from '@hygge/shared';

/**
 * Whether a project is one the project filters over recorded hours hide by
 * default: anything but `active`, so archived and on-hold alike, the same split
 * the time-tracking pickers make (`TIME_TRACKING_PROJECT_QUERY`, HW-874). A
 * state this code does not know yet is hidden too rather than offered.
 *
 * Hidden, not removed: an archived project still holds the hours logged against
 * it, and a report about one is a fair question — so the filters list them
 * behind a "Show archived" toggle instead. Agreed with Kateryna on
 * 2026-09-29. [HW-951]
 */
export function isInactiveProject(status: string): boolean {
  return status !== PROJECT_STATUS.ACTIVE;
}

/** The tag an inactive project carries in a filter ("Archived", "On Hold"), or null for an active one. */
export function inactiveProjectTag(status: string): string | null {
  if (!isInactiveProject(status)) return null;
  return PROJECT_STATUS_LABELS[status] ?? status;
}

/**
 * The projects a filter lists: the active ones, then — only with the toggle on
 * — the inactive ones, each half in the order it arrived, so the everyday list
 * reads the same either way.
 *
 * Whatever is in `keep` stays listed whatever its state: the current selection.
 * A saved report, a link or a click on the Dashboard's projects card can select
 * an archived project with the toggle off, and a filter that dropped it would
 * show a report narrowed by something no longer on screen. An id that names no
 * project ("all") keeps nothing.
 *
 * Generic over the item so the single-choice filters (projects) and the
 * Builder's multi-select (filter options) share one rule.
 */
export function withArchivedHidden<T>(
  items: readonly T[],
  isInactive: (item: T) => boolean,
  idOf: (item: T) => string,
  { showArchived, keep }: { showArchived: boolean; keep: readonly string[] },
): T[] {
  const kept = new Set(keep);
  const active = items.filter((item) => !isInactive(item));
  const inactive = items.filter((item) => isInactive(item) && (showArchived || kept.has(idOf(item))));
  return [...active, ...inactive];
}
